"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.getOrderLinesToJSON = void 0;
/* eslint-disable prefer-spread */
const dayjs = require("dayjs");
const calendar = require("dayjs/plugin/calendar");
const currencyFormatter_1 = require("../currencyFormatter");
const pluralize_1 = require("../pluralize");
const expandOrderItems_1 = require("./expandOrderItems");
const getLatestOrderLineVersion_1 = require("./getLatestOrderLineVersion");
const getOrderCurrency_1 = require("./getOrderCurrency");
const getOrderDisplayCurrency_1 = require("./getOrderDisplayCurrency");
const getOrderDisplayTotal_1 = require("./getOrderDisplayTotal");
const getOrderLineCurrency_1 = require("./getOrderLineCurrency");
const getOrderLineEstimatedCheck_1 = require("./getOrderLineEstimatedCheck");
const getOrderLineDescriptions_1 = require("./getOrderLineDescriptions");
const getOrderLineDisplayTotal_1 = require("./getOrderLineDisplayTotal");
const getOrderLineIcon_1 = require("./getOrderLineIcon");
const getOrderLineSupplier_1 = require("./getOrderLineSupplier");
const getOrderLineTitle_1 = require("./getOrderLineTitle");
const getOrderLineTotal_1 = require("./getOrderLineTotal");
const getOrderTotal_1 = require("./getOrderTotal");
const groupOrderLinesByDate_1 = require("./groupOrderLinesByDate");
const isSummaryItem_1 = require("./isSummaryItem");
const getOrderLineData_1 = require("./getOrderLineData");
const getLinkedLineData_1 = require("./getLinkedLineData");
dayjs.extend(calendar);
const getOrderLinesToJSON = (order, order_id, exchange_rate, transaction_fee, hide_cancelled, include_data = false) => {
    const orderLines = order.order_lines?.filter((line) => {
        if (!line.versions)
            return false;
        const latest_version = (0, getLatestOrderLineVersion_1.getLatestOrderLineVersion)(line.versions);
        if (!latest_version)
            return false;
        if (hide_cancelled &&
            order.status &&
            latest_version.status.includes("CANCELLED") &&
            !order.status.includes("CANCELLED"))
            return false;
        return true;
    });
    if (!orderLines)
        return null;
    if (!order_id)
        return null;
    const expanded_items = (0, expandOrderItems_1.expandOrderItems)(orderLines);
    const lines = {
        order_lines: expanded_items
            .map((item) => {
            if ((0, isSummaryItem_1.isSummaryItem)(item)) {
                return {
                    line_type: "SUMMARY",
                    icon: (0, getOrderLineIcon_1.getOrderLineIcon)(item),
                    type: item.type,
                    region_id: item.region_id,
                    supplier_id: item.supplier_id,
                    order_weight: item.order_weight,
                    start_date: item.start_date,
                    status: item.status,
                    end_date: item.end_date,
                    title: item.title,
                };
            }
            else {
                if (!item.versions)
                    return null;
                const latest_version = (0, getLatestOrderLineVersion_1.getLatestOrderLineVersion)(item.versions);
                if (!latest_version)
                    return null;
                const has_linked_line = expanded_items.find((line) => {
                    if ((0, isSummaryItem_1.isSummaryItem)(line))
                        return false;
                    if (!line.versions)
                        return false;
                    const latest_version = (0, getLatestOrderLineVersion_1.getLatestOrderLineVersion)(line.versions);
                    return latest_version
                        ? latest_version.linked_line_id === item.id
                        : null;
                });
                const is_linked_line = latest_version?.linked_line_id !== undefined &&
                    latest_version?.linked_line_id !== null;
                const linked_line = expanded_items.find((line) => {
                    if ((0, isSummaryItem_1.isSummaryItem)(line))
                        return false;
                    return latest_version?.linked_line_id === line.id;
                });
                return {
                    id: item.id,
                    latest_version_id: latest_version.id,
                    latest_version: latest_version,
                    supplier_id: (0, getOrderLineSupplier_1.getOrderLineSupplier)(item),
                    order_id: item.order_id,
                    order_weight: item.order_weight,
                    type: item.type,
                    region_id: item.region_id,
                    icon: (0, getOrderLineIcon_1.getOrderLineIcon)(item),
                    line_type: "LINE_ITEM",
                    start_date: latest_version.start_date,
                    end_date: latest_version.end_date,
                    title: (0, getOrderLineTitle_1.getOrderLineTitle)(item),
                    reminders: item.reminders,
                    notes: item.order_notes,
                    linked_line: linked_line ? (0, getOrderLineTitle_1.getOrderLineTitle)(linked_line) : null,
                    is_linked_line: is_linked_line ? true : false,
                    has_linked_line: has_linked_line ? true : false,
                    linked_line_data: (0, getLinkedLineData_1.getLinkedLineData)(item, expanded_items),
                    total_price: (0, getOrderLineTotal_1.getOrderLineTotal)(item),
                    total_price_formatted: (0, currencyFormatter_1.currencyFormatter)((0, getOrderLineCurrency_1.getOrderLineCurrency)(item) || "", (0, getOrderLineTotal_1.getOrderLineTotal)(item), false),
                    estimated: (0, getOrderLineEstimatedCheck_1.getOrderLineEstimatedCheck)(item),
                    currency: (0, getOrderLineCurrency_1.getOrderLineCurrency)(item),
                    display_currency: (0, getOrderDisplayCurrency_1.getOrderDisplayCurrency)(exchange_rate),
                    display_total_price: (0, getOrderLineDisplayTotal_1.getOrderLineDisplayTotal)(item, exchange_rate),
                    display_total_price_formatted: (0, currencyFormatter_1.currencyFormatter)((0, getOrderDisplayCurrency_1.getOrderDisplayCurrency)(exchange_rate) || "", (0, getOrderLineDisplayTotal_1.getOrderLineDisplayTotal)(item, exchange_rate), false),
                    data: include_data ? (0, getOrderLineData_1.getOrderLineData)(item) : null,
                    descriptions: (0, getOrderLineDescriptions_1.getOrderLineDescriptions)(item),
                    status: latest_version.status,
                    created_at: dayjs().calendar(dayjs(latest_version.created_at)),
                };
            }
        })
            .sort((a, b) => {
            return (new Date(a.start_date).getTime() - new Date(b.start_date).getTime());
        }),
    };
    let grouped_lines = (0, groupOrderLinesByDate_1.groupOrderLinesByDate)(lines.order_lines);
    // get all the order_lines arrays and flatten them into one array
    let flattened_lines = grouped_lines.map((line) => line.order_lines);
    flattened_lines = [].concat.apply([], flattened_lines);
    const hotelLines = flattened_lines.filter((line) => line.type === "HOTEL" && line.line_type === "LINE_ITEM");
    const transferLines = flattened_lines.filter((line) => line.type === "TRANSFER" && line.line_type === "LINE_ITEM");
    const golfLines = flattened_lines.filter((line) => line.type === "GOLF" && line.line_type === "LINE_ITEM");
    const excursionLines = flattened_lines.filter((line) => line.type === "EXCURSION" && line.line_type === "LINE_ITEM");
    const customLines = flattened_lines
        .filter((line) => {
        // let config = latest_version.config as unknown as CustomOrderLine
        return line.type === "CUSTOM" && line.line_type === "LINE_ITEM";
    })
        .filter((line) => {
        const latest_version = line.latest_version;
        const config = latest_version.config;
        return config?.where_when_who?.type !== "EXTRA";
    });
    const extrasLines = flattened_lines
        .filter((line) => {
        // let config = latest_version.config as unknown as CustomOrderLine
        return line.type === "CUSTOM" && line.line_type === "LINE_ITEM";
    })
        .filter((line) => {
        const latest_version = line.latest_version;
        const config = latest_version.config;
        return config?.where_when_who?.type === "EXTRA";
    });
    // remove the extras from the grouped lines
    grouped_lines = grouped_lines.map((line) => {
        line.order_lines = line.order_lines.filter((line) => {
            if (line.type === "CUSTOM") {
                const latest_version = line.latest_version;
                const config = latest_version.config;
                return config?.where_when_who?.type !== "EXTRA";
            }
            return true;
        });
        return line;
    });
    const summary = [];
    if (hotelLines.length > 0) {
        hotelLines.forEach((line) => {
            const start_date = dayjs(line.start_date);
            const end_date = dayjs(line.end_date);
            const nights = end_date.diff(start_date, "days");
            const config = line?.latest_version.config;
            summary.push({
                title: `Hotel (${config?.where_when_who?.hotel?.name?.en})`,
                currency: line.currency,
                total_price: Number(line.total_price),
                total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(line.currency, Number(line.total_price), false),
                display_currency: line.display_currency,
                display_total_price: Number(line.display_total_price).toFixed(2),
                display_total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(line.display_currency, Number(Number(line.display_total_price).toFixed(2)), false),
                details: `${(0, pluralize_1.pluralize)(nights, "night", "nights")} · ${(0, pluralize_1.pluralize)(config?.where_when_who?.number_of_guests, "guest", "guests")} · ${(0, pluralize_1.pluralize)(config?.rooms?.rooms?.length, "room", "rooms")}`,
            });
        });
    }
    if (golfLines.length > 0) {
        let no_of_players = 0;
        golfLines.forEach((line) => {
            const config = line?.latest_version.config;
            const players = config?.where_when_who?.number_of_golfers || 0;
            if (players > no_of_players) {
                no_of_players = players;
            }
        });
        summary.push({
            title: "Golf",
            currency: golfLines.length > 0 ? golfLines[0].currency : "",
            total_price: golfLines.reduce((acc, a) => acc + Number(a.total_price), 0),
            total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(golfLines.length > 0 ? golfLines[0].currency : "", golfLines.reduce((acc, a) => acc + Number(a.total_price), 0), false),
            display_currency: golfLines.length > 0 ? golfLines[0].display_currency : "",
            display_total_price: Number(golfLines
                .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                .toFixed(2)),
            display_total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(golfLines.length > 0 ? golfLines[0].display_currency : "", Number(golfLines
                .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                .toFixed(2)), false),
            details: `${(0, pluralize_1.pluralize)(golfLines.length, "round", "rounds")} · ${(0, pluralize_1.pluralize)(no_of_players, "player", "players")}`,
        });
    }
    if (transferLines.length > 0) {
        const hotel_transfers = transferLines.filter((line) => {
            const config = line?.latest_version
                .config;
            return config?.transfer_legs?.outbound?.to_type === "Hotel";
        });
        const golf_transfers = transferLines.filter((line) => {
            const config = line?.latest_version
                .config;
            return config?.transfer_legs?.outbound?.to_type === "Course";
        });
        const airport_transfers = transferLines.filter((line) => {
            const config = line?.latest_version
                .config;
            return config?.transfer_legs?.outbound?.to_type === "Airport";
        });
        const other_transfers = transferLines.filter((line) => {
            const config = line?.latest_version
                .config;
            return (config?.transfer_legs?.outbound?.to_type !== "Hotel" &&
                config?.transfer_legs?.outbound?.to_type !== "Course" &&
                config?.transfer_legs?.outbound?.to_type !== "Airport");
        });
        if (hotel_transfers.length > 0) {
            summary.push({
                title: "Hotel Transfers",
                currency: hotel_transfers.length > 0 ? hotel_transfers[0].currency : "",
                total_price: hotel_transfers.reduce((acc, a) => acc + Number(a.total_price), 0),
                total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(hotel_transfers.length > 0 ? hotel_transfers[0].currency : "", hotel_transfers.reduce((acc, a) => acc + Number(a.total_price), 0), false),
                display_currency: hotel_transfers.length > 0 ? hotel_transfers[0].display_currency : "",
                display_total_price: Number(hotel_transfers
                    .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                    .toFixed(2)),
                display_total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(hotel_transfers.length > 0 ? hotel_transfers[0].display_currency : "", Number(hotel_transfers
                    .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                    .toFixed(2)), false),
                details: `${(0, pluralize_1.pluralize)(hotel_transfers.length, "trip", "trips")}`,
            });
        }
        if (golf_transfers.length > 0) {
            summary.push({
                title: "Golf Transfers",
                currency: golf_transfers.length > 0 ? golf_transfers[0].currency : "",
                total_price: golf_transfers.reduce((acc, a) => acc + Number(a.total_price), 0),
                total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(golf_transfers.length > 0 ? golf_transfers[0].currency : "", golf_transfers.reduce((acc, a) => acc + Number(a.total_price), 0), false),
                display_currency: golf_transfers.length > 0 ? golf_transfers[0].display_currency : "",
                display_total_price: Number(golf_transfers
                    .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                    .toFixed(2)),
                display_total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(golf_transfers.length > 0 ? golf_transfers[0].display_currency : "", Number(golf_transfers
                    .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                    .toFixed(2)), false),
                details: `${(0, pluralize_1.pluralize)(golf_transfers.length, "trip", "trips")}`,
            });
        }
        if (airport_transfers.length > 0) {
            summary.push({
                title: "Airport Transfers",
                currency: airport_transfers.length ? airport_transfers[0].currency : "",
                total_price: airport_transfers.reduce((acc, a) => acc + Number(a.total_price), 0),
                total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(airport_transfers.length ? airport_transfers[0].currency : "", airport_transfers.reduce((acc, a) => acc + Number(a.total_price), 0), false),
                display_currency: airport_transfers.length
                    ? airport_transfers[0].display_currency
                    : "",
                display_total_price: Number(airport_transfers
                    .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                    .toFixed(2)),
                display_total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(airport_transfers.length ? airport_transfers[0].display_currency : "", Number(airport_transfers
                    .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                    .toFixed(2)), false),
                details: `${(0, pluralize_1.pluralize)(airport_transfers.length, "trip", "trips")}`,
            });
        }
        if (other_transfers.length > 0) {
            summary.push({
                title: "Other Transfers",
                currency: other_transfers.length ? other_transfers[0].currency : "",
                total_price: other_transfers.reduce((acc, a) => acc + Number(a.total_price), 0),
                total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(other_transfers.length ? other_transfers[0].currency : "", other_transfers.reduce((acc, a) => acc + Number(a.total_price), 0), false),
                display_currency: other_transfers.length
                    ? other_transfers[0].display_currency
                    : "",
                display_total_price: Number(other_transfers
                    .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                    .toFixed(2)),
                display_total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(other_transfers.length ? other_transfers[0].display_currency : "", Number(other_transfers
                    .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                    .toFixed(2)), false),
                details: `${(0, pluralize_1.pluralize)(other_transfers.length, "trip", "trips")}`,
            });
        }
    }
    if (excursionLines.length > 0) {
        summary.push({
            title: "Excursions",
            currency: excursionLines.length > 0 ? excursionLines[0].currency : "",
            total_price: excursionLines.reduce((acc, a) => acc + Number(a.total_price), 0),
            total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(excursionLines.length > 0 ? excursionLines[0].currency : "", excursionLines.reduce((acc, a) => acc + Number(a.total_price), 0), false),
            display_currency: excursionLines.length > 0 ? excursionLines[0].display_currency : "",
            display_total_price: Number(excursionLines
                .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                .toFixed(2)),
            display_total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(excursionLines.length > 0 ? excursionLines[0].display_currency : "", Number(excursionLines
                .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                .toFixed(2)), false),
            details: `${(0, pluralize_1.pluralize)(transferLines.length, "excursion", "excursions")}`,
        });
    }
    if (customLines.length > 0) {
        summary.push({
            title: "Custom",
            currency: customLines.length > 0 ? customLines[0].currency : "",
            total_price: customLines.reduce((acc, a) => acc + Number(a.total_price), 0),
            total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(customLines.length > 0 ? customLines[0].currency : "", customLines.reduce((acc, a) => acc + Number(a.total_price), 0), false),
            display_currency: customLines.length > 0 ? customLines[0].display_currency : "",
            display_total_price: Number(customLines
                .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                .toFixed(2)),
            display_total_price_formatted: Number(customLines
                .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                .toFixed(2)),
            details: `${(0, pluralize_1.pluralize)(customLines.length, "item", "items")}`,
        });
    }
    if (extrasLines.length > 0) {
        summary.push({
            title: "Extras & Addons",
            currency: extrasLines.length > 0 ? extrasLines[0].currency : "",
            total_price: customLines.reduce((acc, a) => acc + Number(a.total_price), 0),
            total_price_formatted: (0, currencyFormatter_1.currencyFormatter)(extrasLines.length > 0 ? extrasLines[0].currency : "", extrasLines.reduce((acc, a) => acc + Number(a.total_price), 0), false),
            display_currency: extrasLines.length > 0 ? extrasLines[0].display_currency : "",
            display_total_price: Number(extrasLines
                .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                .toFixed(2)),
            display_total_price_formatted: Number(extrasLines
                .reduce((acc, line) => acc + Number(line.display_total_price), 0)
                .toFixed(2)),
            details: `${(0, pluralize_1.pluralize)(extrasLines.length, "extra", "extras")}`,
        });
    }
    return {
        order_id: order_id,
        itinerary: {
            items: grouped_lines,
            extras: extrasLines,
            summary: summary,
            fees: {
                credit_card_fee: transaction_fee || 0,
            },
            total: {
                currency: (0, getOrderCurrency_1.getOrderCurrency)(order),
                processing_fee: (0, getOrderTotal_1.getOrderTotal)(order) * ((transaction_fee || 0) / 100),
                processing_fee_formatted: (0, currencyFormatter_1.currencyFormatter)((0, getOrderCurrency_1.getOrderCurrency)(order) || "", (0, getOrderTotal_1.getOrderTotal)(order) * ((transaction_fee || 0) / 100) || 0, false),
                total_price: (0, getOrderTotal_1.getOrderTotal)(order),
                total_price_formatted: (0, currencyFormatter_1.currencyFormatter)((0, getOrderCurrency_1.getOrderCurrency)(order) || "", (0, getOrderTotal_1.getOrderTotal)(order), false),
                display_currency: (0, getOrderDisplayCurrency_1.getOrderDisplayCurrency)(exchange_rate),
                display_total_price: (0, getOrderDisplayTotal_1.getOrderDisplayTotal)(order, exchange_rate),
                display_total_price_formatted: (0, currencyFormatter_1.currencyFormatter)((0, getOrderDisplayCurrency_1.getOrderDisplayCurrency)(exchange_rate), (0, getOrderDisplayTotal_1.getOrderDisplayTotal)(order, exchange_rate), false),
            },
        },
    };
};
exports.getOrderLinesToJSON = getOrderLinesToJSON;
